import { PrimaryButton } from '@/components/buttons/PrimaryButton';
import { useLocation, useNavigate } from 'react-router-dom';
import PencomTextField from '@/components/input/PencomTextField';
import { toast } from '@/utils/toast';
import { useForm } from '@tanstack/react-form';
import { type OTPVerificationForm } from '@/forms/auth/auth.form';
import { PencomRoutes } from '@/router';
import { useResendRegistrationOTPToEmployer, useVerifyRegistrationOTP } from '@/hooks/auth/auth.hook';
import { useOTPRateLimiter } from '@/hooks/useOTPRateLimiter';
// import { PencomBackButton } from '@/components/layout/PencomBackButton';

const formatResendTimer = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const EmployerRegistrationVerificationPage = () => {
    const navigate = useNavigate();
    const data = useLocation().state as {
        contactEmail?: string;
        employerCode: string;
    };

    const verifyRegistrationOTPMutation = useVerifyRegistrationOTP();
    const resendRegistrationOTPMutation = useResendRegistrationOTPToEmployer();
    const { canResend, timeRemaining, startCooldown } = useOTPRateLimiter(60, true);
    const contactEmail = data?.contactEmail;
    const emailDisplay = contactEmail || 'your email';

    const form = useForm({
        defaultValues: {
            otp: '',
        } as OTPVerificationForm,
        onSubmit: async ({ value }) => {
            if (!contactEmail || !data?.employerCode) return;
            verifyRegistrationOTPMutation.mutate(
                { employerCode: data.employerCode, otpCode: value.otp },
                {
                    onSuccess: () => {
                        toast.success('OTP verified successfully');
                        navigate(PencomRoutes.EMPLOYER_REP_DETAILS, { state: data });
                    },
                    onError: (error) => {
                        form.setFieldValue('otp', '');
                        toast.error(error.message || 'Invalid or expired code. Please try again.');
                    },
                }
            );
        },
    });

    const handleResendOTP = () => {
        if (!canResend || !contactEmail || !data?.employerCode) return;
        resendRegistrationOTPMutation.mutate(data.employerCode, {
            onSuccess: () => {
                toast.success('OTP sent successfully');
                startCooldown();
            },
            onError: (error) => {
                toast.error(error.message || 'Failed to send OTP. Please try again.');
            },
        });
    };

    const handleReportIssue = () => {
        navigate(PencomRoutes.REPORT_DISCREPENCY, { state: data });
    };

    return (
        <div className="w-full flex flex-col">
            {/* <PencomBackButton title="Back" /> */}

            <h2 className="font-clash-display font-semibold text-[40px] text-center text-medium-dark-grey leading-[1.25] tracking-[-0.6px] mt-4">
                Check your email
            </h2>
            <p className="font-clash-display text-center text-medium-dark-grey leading-[25px] tracking-[-0.08px] mt-3">
                Enter the 6-digit code sent to <span className="font-semibold">{emailDisplay}</span>
            </p>
            <p className="font-clash-display text-sm text-center text-medium-dark-grey leading-[18px] tracking-[-0.06px] mt-2">
                Not your company email?{' '}
                <button type="button" className="font-semibold text-primary-button underline cursor-pointer hover:opacity-90" onClick={handleReportIssue}>
                    Report issue
                </button>{' '}
                to PenCom.
            </p>

            <div className="flex flex-col gap-10 mt-10">
                <form.Field
                    name="otp"
                    validators={{
                        onChange: ({ value }) => {
                            if (value && value.length > 0 && value.length !== 6) return 'OTP must be 6 digits';
                            return undefined;
                        },
                    }}
                    children={(field) => (
                        <div className="flex flex-col gap-2">
                            <PencomTextField
                                id="verification-code"
                                label=""
                                type="otp"
                                value={field.state.value}
                                onChange={(e) => {
                                    if (verifyRegistrationOTPMutation.error) verifyRegistrationOTPMutation.reset();
                                    if (resendRegistrationOTPMutation.error) resendRegistrationOTPMutation.reset();
                                    field.handleChange(e.target.value);
                                    if (e.target.value.length === 6) {
                                        setTimeout(() => form.handleSubmit(), 0);
                                    }
                                }}
                                otpLength={6}
                                otpMasked={true}
                                otpMaskChar="•"
                                otpMaskDelay={1500}
                                error={
                                    field.state.meta.errors.length > 0 ||
                                    !!verifyRegistrationOTPMutation.error
                                }
                                helperText={
                                    field.state.meta.errors.length > 0
                                        ? (field.state.meta.errors[0] as string)
                                        : verifyRegistrationOTPMutation.error
                                            ? 'Invalid or expired code. Please try again.'
                                            : undefined
                                }
                            />
                            {field.state.meta.errors.length > 0 && !verifyRegistrationOTPMutation.error && (
                                <div className="flex items-center gap-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-600 font-clash-display">{field.state.meta.errors[0]}</p>
                                </div>
                            )}
                        </div>
                    )}
                />

                {(resendRegistrationOTPMutation.error || verifyRegistrationOTPMutation.error) && (
                    <div className="flex items-center gap-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600 font-clash-display">
                            {verifyRegistrationOTPMutation.error
                                ? 'Invalid or expired code. Please try again.'
                                : resendRegistrationOTPMutation.error?.message ||
                                'Something went wrong. Please try again.'}
                        </p>
                    </div>
                )}

                <form.Subscribe
                    children={(state) => {
                        const isDisabled =
                            !state.values.otp ||
                            state.values.otp.length !== 6 ||
                            verifyRegistrationOTPMutation.isPending;
                        return (
                            <PrimaryButton
                                className="w-full"
                                title={verifyRegistrationOTPMutation.isPending ? 'Verifying...' : 'Verify'}
                                onClick={() => form.handleSubmit()}
                                disabled={isDisabled}
                                isLoading={verifyRegistrationOTPMutation.isPending}
                            />
                        );
                    }}
                />

                <div className="bg-white border border-stroke rounded-lg p-4 flex flex-col gap-2.5">
                    <div className="flex items-center gap-2">
                        <img src="/assets/icons/info-outline.svg" alt="" className="w-4 h-4 shrink-0" aria-hidden />
                        <p className="font-clash-display font-medium text-sm text-medium-dark-grey leading-[18px] tracking-[-0.07px]">
                            Didn&apos;t get code?
                        </p>
                    </div>
                    <p className="font-clash-display font-normal text-sm text-medium-grey leading-[18px] tracking-[-0.07px]">
                        Please check your SPAM or JUNK folder if you don&apos;t see it. <br /> You can request a new code below.
                    </p>
                    <p className="font-clash-display font-normal text-xs text-medium-dark-grey leading-[25px] tracking-[-0.06px]">
                        {canResend && !resendRegistrationOTPMutation.isPending ? (
                            <button
                                type="button"
                                onClick={handleResendOTP}
                                className="text-primary-button font-semibold hover:underline cursor-pointer"
                            >
                                Resend the code
                            </button>
                        ) : (
                            <>Resend the code in {formatResendTimer(timeRemaining)}</>
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default EmployerRegistrationVerificationPage;
